import { ExclamationCircleOutlined, CloseCircleOutlined, FormOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message, Modal, Space, Tooltip } from 'antd';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRY_THEN_BUY_FRAUD } from '../../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { API_URLS } from '../../../../constants/urls/api-urls';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import {
  postCancelTTBFraudRepository,
  postNoteTTBFraudRepository,
} from '../../../../repository/mytm/try-then-buy-fraud/ttbFraudRepository';
import { btnActive } from '../../../../styles';
import { TTBFraudStatus, TTBFraudType } from '../../../../types/tryThenBuyFraud';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { ModalReasonInput } from '../modals/ModalReasonInput';
import { ModalNoteInput } from '../modals/ModalNoteInput';
import { getDetectionHighlightTTBFraud } from '../../../../utils/transfer/ttb-fraud';
import { IconBlacklist } from '../../../../assets';

interface ActionsColumnProps {
  record: TTBFraudType;
  onRefresh: () => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(TRY_THEN_BUY_FRAUD);
  const { record, onRefresh } = props;
  const userContext = useContext(UserContext);

  const [isOpenModalCancel, setIsOpenModalCancel] = useState(false);
  const [isOpenModalNote, setIsOpenModalNote] = useState(false);
  const [loading, setLoading] = useState(false);

  const onCloseCancelModal = useCallback(() => {
    setIsOpenModalCancel(false);
  }, []);
  const onCloseNoteModal = useCallback(() => {
    setIsOpenModalNote(false);
  }, []);
  const onOpenCancelModal = useCallback(() => {
    setIsOpenModalCancel(true);
  }, []);
  const onOpenNoteModal = useCallback(() => {
    setIsOpenModalNote(true);
  }, []);
  const canCancel =
    record.status !== TTBFraudStatus.CANCELLED &&
    record.status !== TTBFraudStatus.PRODUCT_RETURNED &&
    record.status !== TTBFraudStatus.CHARGED &&
    record.status !== TTBFraudStatus.BOUGHT &&
    !!record.orderId;

  const canBlacklist = !record.userBlackList && !canCancel;

  const handleSubmitNote = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.note')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseNoteModal();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postNoteTTBFraudRepository(API_URLS.NOTE_TRY_THEN_BUY_FRAUD, {
            ttbFraudId: record.id,
            note: value.note,
          })
            .then(() => {
              onRefresh();
              message.success(t('message.updateSuccess'));
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };

  const handleSubmitBlacklist = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.blacklistConfirm')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postCancelTTBFraudRepository(API_URLS.CANCEL_TRY_THEN_BUY_FRAUD, {
            userId: record.userId,
            isBlack: true,
          })
            .then(r => {
              onRefresh();
              message.success(t('message.updateSuccess'));
            })
            .catch(e => {
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setLoading(false);
            });
        };

        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleSubmitCancel = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.cancel')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseCancelModal();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postCancelTTBFraudRepository(API_URLS.CANCEL_TRY_THEN_BUY_FRAUD, {
            orderId: record.orderId,
            reasonCancel: value.reason,
            userId: record.userId,
            isBlack: !!value.blacklist,
          })
            .then(() => {
              onRefresh();
              message.success(t('message.updateSuccess'));
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };

  return (
    <>
      <StyledContainer>
        <Space size="small">
          <div className={`preview-note isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
            {record.note}
          </div>
          <Tooltip title={t('table.tooltip.note')}>
            <Button className="note" loading={loading} icon={<FormOutlined />} onClick={onOpenNoteModal} />
            <ModalNoteInput
              onSubmit={handleSubmitNote}
              isOpenModal={isOpenModalNote}
              onClose={onCloseNoteModal}
              title={t('form.noteTTBFraud')}
              content={record.note}
              placeholder={t('form.placeholderEnter')}
            />
          </Tooltip>
          {canBlacklist && (
            <Tooltip title={t('table.tooltip.blacklist')}>
              <Button
                className="blacklist"
                loading={loading}
                icon={<IconBlacklist />}
                onClick={handleSubmitBlacklist}
              />
            </Tooltip>
          )}
          {canCancel && (
            <Tooltip title={t('table.tooltip.cancel')}>
              <Button className="cancel" loading={loading} icon={<CloseCircleOutlined />} onClick={onOpenCancelModal} />
              <ModalReasonInput
                onSubmit={handleSubmitCancel}
                isOpenModal={isOpenModalCancel}
                onClose={onCloseCancelModal}
                title={t('form.cancelTTBFraud')}
                label={t('form.cancelReason')}
                placeholder={t('form.placeholderEnter')}
                validateMessage=""
              />
            </Tooltip>
          )}
        </Space>
      </StyledContainer>
    </>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}

  .preview-note {
    width: 100px;
    display: -webkit-box;
    overflow: hidden;
    text-align: left;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
